import React from "react";
import { Link } from "react-router-dom";
import { toTitleCase } from "../../../../utils/helpers";
import { CARD_TAG_CHIP_LIMIT } from "../../../../utils/tagDisplay";
import BrandLogoBadge from "./BrandLogoBadge";

export type PromoTourCardVariant = "compact" | "grid" | "stacked";

const formatOfferTitle = (value: string): string =>
  value
    .split(" ")
    .map((word) => (/^\d+D\d+N$/i.test(word) ? word.toUpperCase() : toTitleCase(word)))
    .join(" ");

export interface PromoTourCardProps {
  image: string;
  title: string;
  brandLogo?: string;
  brandName?: string;
  brandDescription?: string;
  ribbon?: string;
  // Tags after the ribbon: the first two show as chips, the rest collapse into "+N More".
  tags?: string[];
  destination?: string;
  departFrom?: string;
  guaranteed?: boolean;
  originalPrice?: string;
  save?: string;
  price: string;
  priceSuffix?: string;
  footnote?: string;
  href?: string;
  variant?: PromoTourCardVariant;
}

export default function PromoTourCard({
  image,
  title,
  brandLogo,
  brandName,
  brandDescription,
  ribbon,
  tags = [],
  destination,
  departFrom,
  guaranteed,
  originalPrice,
  save,
  price,
  priceSuffix = "/ person",
  footnote,
  href,
  variant = "compact",
}: PromoTourCardProps) {
  const chips = tags.slice(0, CARD_TAG_CHIP_LIMIT);
  const moreCount = tags.length - chips.length;

  const content = (
    <>
      <div className="v2-offer-card__media">
        {image && <img src={image} alt={title} />}
        {ribbon && <span className="v2-offer-card__badge">{ribbon}</span>}
        <BrandLogoBadge logo={brandLogo} description={brandDescription} />
      </div>
      <div className="v2-offer-card__body">
        <div className="v2-offer-card__content">
          {(destination || departFrom) && (
            <div className="v2-offer-card__loc">
              {destination && toTitleCase(destination)}
              {destination && departFrom && <span className="v2-offer-card__dot">•</span>}
              {departFrom && <span>from {departFrom}</span>}
            </div>
          )}
          {brandName && <div className="v2-offer-card__brand-name">{brandName}</div>}
          <h3 className="v2-offer-card__title">{formatOfferTitle(title)}</h3>
          <div className="v2-offer-card__tag-row">
            {guaranteed && <span className="v2-offer-card__tag">Guaranteed Departure</span>}
          </div>
          {chips.length > 0 && (
            <div className="v2-offer-card__promo-tags">
              {chips.map((tag) => (
                <span className="v2-offer-card__promo-tag" key={tag}>{tag}</span>
              ))}
              {moreCount > 0 && <span className="v2-offer-card__promo-tag">+{moreCount} More</span>}
            </div>
          )}
        </div>
        <div className="v2-offer-card__pricing">
          <span className="v2-offer-card__from">
            From {originalPrice && <span className="v2-offer-card__orig">{originalPrice}</span>}
          </span>
          {save && <span className="v2-offer-card__save-tag">Save {save}</span>}
        </div>
        <div className="v2-offer-card__price">
          {price} <span>{priceSuffix}</span>
        </div>
        {footnote && <div className="v2-offer-card__footnote">{footnote}</div>}
      </div>
    </>
  );

  const className = `v2-offer-card v2-offer-card--${variant}`;

  return href ? (
    <Link to={href} className={className}>
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  );
}
